import { useEffect, useRef, type RefObject } from 'react'

/**
 * Make an editor fill the window between whatever sits above it (the top nav, an AI-health
 * banner, a story return bar) and the queue strip at the bottom, so the page itself never
 * scrolls and only the editor's own panels do.
 *
 * The hook measures the element's page-top offset into a CSS variable (`--fill-top` unless told
 * otherwise) and re-measures it on resize and whenever anything earlier in the page changes size.
 * The bottom comes from `--shell-bottom`, which `AppShell` publishes and which already includes an
 * open queue panel. Pair it with {@link VIEWPORT_FILL_CLASS} (or the same `calc` written out):
 *
 * ```tsx
 * const ref = useViewportFill<HTMLDivElement>(loaded)
 * return <div ref={ref} className={VIEWPORT_FILL_CLASS}>…</div>
 * ```
 *
 * With {@link VIEWPORT_FILL_CLASS} the height is exact from `lg` up and only a minimum below it
 * (a phone stacks the panels and scrolls the page); {@link VIEWPORT_FIT_CLASS} is exact at every
 * width, for layouts whose stacked panels scroll inside themselves.
 */
export function useViewportFill<T extends HTMLElement = HTMLDivElement>(active = true, varName = '--fill-top'): RefObject<T | null> {
  const ref = useRef<T | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!active || !el) return
    const measure = (): void => {
      const top = Math.max(0, Math.round(el.getBoundingClientRect().top + window.scrollY))
      el.style.setProperty(varName, `${top}px`)
    }
    measure()
    window.addEventListener('resize', measure)
    let ro: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure)
      // Everything that can push the editor down: the earlier siblings of each ancestor.
      for (let node: Element | null = el; node && node !== document.body; node = node.parentElement) {
        for (let prev = node.previousElementSibling; prev; prev = prev.previousElementSibling) ro.observe(prev)
      }
    }
    return () => {
      window.removeEventListener('resize', measure)
      ro?.disconnect()
    }
  }, [active, varName])
  return ref
}

/**
 * The height contract for `--fill-top` callers: at least the window below the measured top and
 * above the queue strip on small screens (the page may scroll there), exactly that from `lg` up.
 * Written out in full so Tailwind sees every class.
 */
export const VIEWPORT_FILL_CLASS =
  'min-h-[calc(100dvh-var(--fill-top,3.5rem)-var(--shell-bottom,2.5rem))] lg:h-[calc(100dvh-var(--fill-top,3.5rem)-var(--shell-bottom,2.5rem))] lg:min-h-[480px]'

/**
 * The stricter contract, for an editor whose small-screen layout stacks into panels that scroll
 * on their own (the stage annotator): **exactly** the window below the measured top and above
 * the queue strip at every width, so a phone held upright (390×844) does not scroll the page
 * either. Only a window too short for the editor at all (a phone on its side) falls back to the
 * floor height, and the page scrolls then rather than hiding anything.
 */
export const VIEWPORT_FIT_CLASS =
  'h-[calc(100dvh-var(--fill-top,3.5rem)-var(--shell-bottom,2.5rem))] min-h-[520px] lg:min-h-[480px]'
